'use client';

import { useEffect, useRef, useState } from 'react';

// Ticks and stale subscriptions pass a fresh timestamp to each component.
type Subscriber = (now: number) => void;

type TickerEntry = {
  subscribers: Set<Subscriber>;
  timer: ReturnType<typeof setTimeout> | null;
  nextTickAt: number | null;
};

// Shared ticker registry, keyed by interval. All consumers with the same
// intervalMs share ONE timer instead of each mounting their own
// (this replaces one timer per inbox row). Entries are created lazily
// on first subscribe, so nothing touches browser APIs at module load (SSR-safe).
const tickers = new Map<number, TickerEntry>();

let isVisibilityListenerAttached = false;

function notify(entry: TickerEntry) {
  const now = Date.now();
  for (const subscriber of entry.subscribers) {
    subscriber(now);
  }
}

function startTimer(intervalMs: number, entry: TickerEntry, delay = intervalMs) {
  const nextTickAt = Date.now() + delay;
  if (entry.nextTickAt !== null && entry.nextTickAt <= nextTickAt) {
    return;
  }
  stopTimer(entry);
  entry.nextTickAt = nextTickAt;
  entry.timer = setTimeout(() => {
    entry.timer = null;
    entry.nextTickAt = null;
    notify(entry);
    startTimer(intervalMs, entry);
  }, delay);
}

function stopTimer(entry: TickerEntry) {
  if (entry.timer !== null) {
    clearTimeout(entry.timer);
  }
  entry.timer = null;
  entry.nextTickAt = null;
}

function handleVisibilityChange() {
  if (document.hidden) {
    // Pause all tickers while the tab is hidden - nothing is visible to update.
    for (const entry of tickers.values()) {
      stopTimer(entry);
    }
    return;
  }
  // Tab became visible again: catch subscribers up immediately with a fresh
  // timestamp (timers were paused, so displayed values may be stale), then resume.
  for (const [intervalMs, entry] of tickers) {
    notify(entry);
    startTimer(intervalMs, entry);
  }
}

function subscribe(intervalMs: number, subscriber: Subscriber, reading: number): () => void {
  let entry = tickers.get(intervalMs);
  if (!entry) {
    entry = { subscribers: new Set(), timer: null, nextTickAt: null };
    tickers.set(intervalMs, entry);
  }
  entry.subscribers.add(subscriber);

  // Next.js hides pages the user navigated away from in <Activity>, which keeps
  // their state but drops their subscriptions, so a page shown again subscribes
  // with the reading it was hidden with. A reading that has missed a tick catches
  // up now instead of showing hours-old times until the next tick; a fresh
  // mount's reading is current, so it keeps it and renders nothing extra.
  // A resumed reading that is still fresh ticks after its remaining delay.
  const now = Date.now();
  const age = Math.max(0, now - reading);
  if (age >= intervalMs) {
    subscriber(now);
  }
  const delay = age >= intervalMs ? intervalMs : intervalMs - age;

  const hasDocument = typeof document !== 'undefined';

  // Don't start ticking while hidden; the visibility handler resumes it.
  if (!hasDocument || !document.hidden) {
    startTimer(intervalMs, entry, delay);
  }

  // Only listen for visibility changes while there are subscribers.
  if (hasDocument && !isVisibilityListenerAttached) {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    isVisibilityListenerAttached = true;
  }

  return () => {
    entry.subscribers.delete(subscriber);
    if (entry.subscribers.size === 0) {
      stopTimer(entry);
      tickers.delete(intervalMs);
    }
    if (tickers.size === 0 && isVisibilityListenerAttached) {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      isVisibilityListenerAttached = false;
    }
  };
}

/**
 * Returns the current epoch milliseconds, updated every `intervalMs` via a
 * shared module-level ticker (one timer per distinct intervalMs, no matter
 * how many components subscribe). Pauses while the document is hidden and
 * ticks immediately on becoming visible again. A component that subscribes
 * again after missing a tick, like a page Next.js shows again after hiding it
 * in <Activity>, catches up at once.
 */
export function useNow(intervalMs = 60_000): number {
  const [now, setNow] = useState(() => Date.now());
  const latestReading = useRef(now);

  useEffect(
    () =>
      subscribe(
        intervalMs,
        (timestamp) => {
          latestReading.current = timestamp;
          setNow(timestamp);
        },
        latestReading.current,
      ),
    [intervalMs],
  );

  return now;
}
